import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { FulfillmentStage, OrderItemView, OrderState, OrderView } from '../../shared/types';
import { Badge, Button, Card, Checkbox, EmptyState, Field, HeroSelectField, Input, LoadingScreen, Modal, PageHeader, Textarea, cx } from '../components/Ui';
import { getCriticality } from '../components/control-tower/control-tower-utils';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  useBusinessBranches,
  useConfirmDeliveryLocation,
  useDeliveryTokenMutation,
  useFulfillmentDeliveryComplete,
  useFulfillmentCorrection,
  useFulfillmentHandoff,
  useLateFulfillmentCancellation,
  useOrders,
} from '../hooks/businessData';
import { useDeliveryLinks } from '../hooks/useDeliveryLinks';
import { useSse } from '../hooks/useSse';
import { ApiClientError, apiRequest, jsonBody } from '../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { formatLocalMinor, formatUsd } from '../lib/currency';
import { deliveryLinkUrl, phoneForWhatsapp, whatsappMessage } from '../lib/deliveryLink';

export type DeliveryFilter = 'todas' | 'delivery' | 'pickup' | 'local';

/**
 * Quick filters for reception: status/stage buckets (nuevos, preparación,
 * listos) plus origin/modality shortcuts (delivery, mesa). Multi-selectable
 * with OR semantics; an empty selection shows every order.
 */
export type ReceptionQuickFilter = 'nuevos' | 'preparacion' | 'listos' | 'delivery' | 'mesa';

export const QUICK_FILTER_LABELS: Record<ReceptionQuickFilter, string> = {
  nuevos: 'Nuevos',
  preparacion: 'En preparación',
  listos: 'Listos',
  delivery: 'Delivery',
  mesa: 'Mesa',
};

const QUICK_FILTER_ORDER: ReceptionQuickFilter[] = ['nuevos', 'preparacion', 'listos', 'delivery', 'mesa'];

const GOOGLE_MAPS_HOSTS = new Set(['google.com', 'www.google.com', 'maps.google.com', 'maps.app.goo.gl']);

interface EditableItem {
  itemId: string;
  productName: string;
  quantity: number;
  ingredients: Array<{
    ingredientId: string;
    name: string;
    included: boolean;
    selectable: boolean;
  }>;
}

const statusTone: Record<OrderState, 'amber' | 'blue' | 'green' | 'red' | 'purple'> = {
  pendiente: 'amber',
  preparacion: 'blue',
  completado: 'green',
  cancelado: 'red',
  fantasma: 'purple',
};

const fulfillmentLabels: Record<FulfillmentStage, string> = {
  kitchen: 'En cocina',
  delivery_prep: 'En preparación (reparto)',
  ready: 'Listo para entregar',
  transit: 'En reparto',
  delivered: 'Entregado',
  handed_off: 'Retirado / entregado en mostrador',
  cancelled: 'Cancelado',
};

const fulfillmentTone: Record<FulfillmentStage, 'amber' | 'blue' | 'green' | 'red' | 'purple'> = {
  kitchen: 'blue',
  delivery_prep: 'blue',
  ready: 'amber',
  transit: 'purple',
  delivered: 'green',
  handed_off: 'green',
  cancelled: 'red',
};

function isFulfillmentStage(value: string | null): value is FulfillmentStage {
  return value === 'kitchen' || value === 'delivery_prep' || value === 'ready' || value === 'transit' || value === 'delivered' || value === 'handed_off' || value === 'cancelled';
}

function OptionSummary({ options }: { options: OrderItemView['options'] }) {
  if (!options.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {options.map((option) => (
        <span key={option.id} className="rounded-full bg-sky-100 px-2.5 py-1 text-xs font-semibold text-sky-900">
          {option.groupName}: {option.quantity > 1 ? `${option.quantity}× ` : ''}{option.optionName}
        </span>
      ))}
    </div>
  );
}

function ItemConfiguration({ item }: { item: OrderItemView }) {
  const excluded = item.ingredients.filter((ingredient) => ingredient.excluded);
  return (
    <div className="mt-2 grid gap-2">
      <OptionSummary options={item.options} />
      {excluded.length > 0 && (
        <div className="rounded-lg bg-rose-50 px-2.5 py-1.5 text-xs font-bold text-rose-800">
          Sin: {excluded.map((ingredient) => ingredient.name).join(', ')}
        </div>
      )}
      {item.components.length > 0 && (
        <div className="grid gap-2 border-l-2 border-violet-200 pl-3">
          {item.components.map((component) => (
            <div key={component.id} className="rounded-lg bg-white p-2 ring-1 ring-slate-200">
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="font-black text-violet-800">{component.componentName}</span>
                <span className="font-bold">{component.quantity}× {component.productName}</span>
                {component.variantName && <span className="rounded-full bg-violet-100 px-2 py-0.5 font-semibold text-violet-800">{component.variantName}</span>}
                <span className="text-slate-500">Zona {component.preparationZone}</span>
              </div>
              <OptionSummary options={component.options} />
              {component.excludedIngredients.length > 0 && <p className="mt-2 text-xs font-bold text-rose-700">Sin: {component.excludedIngredients.map((ingredient) => ingredient.name).join(', ')}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SavedItemConfiguration({ order, itemId }: { order: OrderView; itemId: string }) {
  const item = order.items.find((orderItem) => orderItem.id === itemId);
  return item ? <ItemConfiguration item={item} /> : null;
}

function OrderNumber({ value }: { value: number }) {
  return (
    <div className="grid h-20 min-w-20 place-items-center rounded-2xl bg-slate-950 px-4 text-white shadow-sm">
      <div className="text-center"><div className="text-[10px] font-bold uppercase tracking-[.2em] text-slate-400">Pedido</div><div className="text-3xl font-black">#{String(value).padStart(2, '0')}</div></div>
    </div>
  );
}

export function filterOrdersByDelivery(orders: OrderView[], deliveryFilter: DeliveryFilter): OrderView[] {
  if (deliveryFilter === 'todas') return orders;
  return orders.filter((order) => order.deliveryType === deliveryFilter);
}

/**
 * Pure quick-filter derivation for reception. An order is shown when it
 * satisfies ANY active filter; with no active filters every order is shown.
 * Buckets mirror the page sections: 'nuevos' = pending and not yet in a
 * fulfillment stage, 'preparacion' = cooking/delivery-prep stages (or the
 * preparacion status), 'listos' = ready or later fulfillment stages (or the
 * completado status), 'delivery'/'mesa' = modality/origin shortcuts. The
 * "Requiere acción" panel intentionally ignores these filters.
 */
export function matchesReceptionQuickFilter(order: OrderView, activeFilters: readonly ReceptionQuickFilter[]): boolean {
  if (activeFilters.length === 0) return true;
  return activeFilters.some((filter) => {
    switch (filter) {
      case 'nuevos':
        return order.status === 'pendiente' && order.fulfillmentStage === null;
      case 'preparacion':
        return order.status === 'preparacion' || order.fulfillmentStage === 'kitchen' || order.fulfillmentStage === 'delivery_prep';
      case 'listos':
        return order.status === 'completado'
          || order.fulfillmentStage === 'ready'
          || order.fulfillmentStage === 'transit'
          || order.fulfillmentStage === 'delivered'
          || order.fulfillmentStage === 'handed_off';
      case 'delivery':
        return order.deliveryType === 'delivery';
      case 'mesa':
        return order.origin === 'mesero' || Boolean(order.tableId);
    }
  });
}

function isAllowedGoogleMapsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.port === '' && url.username === '' && url.password === ''
      && GOOGLE_MAPS_HOSTS.has(url.hostname.toLowerCase());
  } catch {
    return false;
  }
}

export function extractAllowedGoogleMapsUrl(value: string | null | undefined): string | null {
  const matches = value?.match(/https:\/\/[^\s<>"']+/g) ?? [];
  for (const match of matches) {
    const candidate = match.replace(/[.,;:)]+$/g, '');
    if (isAllowedGoogleMapsUrl(candidate)) return candidate;
  }
  return null;
}

export function canIssueDeliveryLink(deliveryType: OrderView['deliveryType'], stage: FulfillmentStage): boolean {
  return deliveryType === 'delivery' && (stage === 'ready' || stage === 'transit');
}

export interface ReceptionActionGroup {
  nuevos: OrderView[];
  demorados: OrderView[];
  errores: Array<{ order: OrderView; message: string }>;
  stockInsuficiente: OrderView[];
}

function openActionIssuesOf(order: OrderView, type: 'stock_insuficiente' | 'fulfillment_error') {
  return (order.actionIssues ?? []).filter((issue) => issue.status === 'abierta' && issue.type === type);
}

/**
 * Human-readable stock shortage summary for the panel: prefers the backend
 * computed shortages, falls back to persisted stock_insuficiente issue details
 * (or message) so the panel reflects the issue even after a refetch.
 */
export function stockShortageSummary(order: OrderView): string {
  const shortages = order.stockShortages ?? [];
  if (shortages.length > 0) {
    return shortages.map((shortage) => `${shortage.productName} (sol. ${shortage.requested} / disp. ${shortage.available})`).join(', ');
  }
  const issues = openActionIssuesOf(order, 'stock_insuficiente');
  const summaries = issues
    .flatMap((issue) => (Array.isArray(issue.details) ? issue.details : []))
    .map((detail) => {
      if (typeof detail === 'object' && detail !== null && 'productName' in detail) {
        const entry = detail as { productName?: unknown; requested?: unknown; available?: unknown };
        return `${String(entry.productName ?? '')} (sol. ${String(entry.requested ?? '?')} / disp. ${String(entry.available ?? '?')})`;
      }
      return null;
    })
    .filter((value): value is string => Boolean(value));
  if (summaries.length > 0) return [...new Set(summaries)].join(', ');
  return issues[0]?.message?.trim() || 'Stock insuficiente';
}

/**
 * Pure derivation for the "Requiere acción" panel. Demorados are pending orders
 * past the shared warning threshold (getCriticality); errores merge the local
 * fulfillment error state (session-level, immediate feedback) with the backend
 * persisted open fulfillment_error issues (survive refreshes); stock
 * insuficiente comes from the backend computed `order.stockShortages` or the
 * persisted open stock_insuficiente issues.
 */
export function computeReceptionActionItems(
  orders: OrderView[],
  fulfillmentErrors: Record<string, string>,
  now: number,
): ReceptionActionGroup {
  const nuevos = orders.filter((order) => order.status === 'pendiente');
  const demorados = nuevos.filter((order) => getCriticality(order, now).level !== 'normal');
  const errores: ReceptionActionGroup['errores'] = [];
  for (const order of orders) {
    const localMessage = fulfillmentErrors[order.id];
    if (localMessage?.trim()) {
      errores.push({ order, message: localMessage });
      continue;
    }
    const persistedMessage = openActionIssuesOf(order, 'fulfillment_error')[0]?.message;
    if (persistedMessage?.trim()) errores.push({ order, message: persistedMessage });
  }
  const stockInsuficiente = nuevos.filter((order) =>
    (order.stockShortages?.length ?? 0) > 0
    || openActionIssuesOf(order, 'stock_insuficiente').length > 0,
  );
  return { nuevos, demorados, errores, stockInsuficiente };
}

function orderShortLabel(order: OrderView): string {
  return `Pedido #${String(order.correlation).padStart(2, '0')}`;
}

export function ReceptionQuickFilters({ active, onToggle }: {
  active: readonly ReceptionQuickFilter[];
  onToggle(filter: ReceptionQuickFilter | null): void;
}) {
  const chipClass = (pressed: boolean) => cx(
    'inline-flex min-h-9 items-center rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors',
    pressed ? 'bg-slate-950 text-white shadow-sm' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-900',
  );
  return (
    <div role="group" aria-label="Filtros rápidos" className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-black uppercase tracking-wide text-slate-500">Filtros rápidos</span>
      <button type="button" aria-pressed={active.length === 0} className={chipClass(active.length === 0)} onClick={() => onToggle(null)}>Todos</button>
      {QUICK_FILTER_ORDER.map((filter) => {
        const pressed = active.includes(filter);
        return (
          <button key={filter} type="button" aria-pressed={pressed} className={chipClass(pressed)} onClick={() => onToggle(filter)}>
            {QUICK_FILTER_LABELS[filter]}
          </button>
        );
      })}
    </div>
  );
}

export function RequiereAccionPanel({ groups }: { groups: ReceptionActionGroup }) {
  const { nuevos, demorados, errores, stockInsuficiente } = groups;
  const total = nuevos.length + demorados.length + errores.length + stockInsuficiente.length;
  const scrollToOrder = (orderId: string) => {
    document.getElementById(`order-${orderId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const scrollToPending = () => {
    document.getElementById('pending-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  if (total === 0) return null;
  const stats: Array<{ label: string; count: number; tone: string; onClick?: () => void }> = [
    { label: 'Nuevos', count: nuevos.length, tone: 'bg-amber-50 text-amber-900 ring-amber-200', onClick: scrollToPending },
    { label: 'Demorados', count: demorados.length, tone: demorados.some((order) => getCriticality(order, Date.now()).level === 'critical') ? 'bg-rose-50 text-rose-900 ring-rose-200' : 'bg-orange-50 text-orange-900 ring-orange-200', onClick: scrollToPending },
    { label: 'Con error', count: errores.length, tone: 'bg-rose-50 text-rose-900 ring-rose-200' },
    { label: 'Stock insuficiente', count: stockInsuficiente.length, tone: 'bg-rose-50 text-rose-900 ring-rose-200' },
  ];
  return (
    <section aria-label="Requiere acción" className="mb-8 rounded-[1.5rem] border border-amber-200 bg-amber-50/60 p-4 sm:p-5">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-lg font-black text-slate-950">⚠ Requiere acción</h2>
        <Badge tone="amber">{total}</Badge>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <button
            key={stat.label}
            type="button"
            onClick={stat.onClick}
            disabled={!stat.onClick}
            className={cx('rounded-2xl p-4 text-left ring-1 transition-opacity', stat.tone, stat.onClick && 'cursor-pointer hover:opacity-80')}
          >
            <div className="text-3xl font-black">{stat.count}</div>
            <div className="text-sm font-bold">{stat.label}</div>
          </button>
        ))}
      </div>
      {(demorados.length > 0 || errores.length > 0 || stockInsuficiente.length > 0) && (
        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          {demorados.length > 0 && (
            <div className="rounded-2xl bg-white p-3 ring-1 ring-orange-200">
              <p className="mb-2 text-xs font-black uppercase tracking-wide text-orange-700">Demorados</p>
              <ul className="grid gap-1.5">
                {demorados.slice(0, 5).map((order) => (
                  <li key={order.id}>
                    <button type="button" onClick={() => scrollToOrder(order.id)} className="w-full rounded-xl bg-orange-50 px-3 py-2 text-left text-sm font-semibold text-orange-900 hover:bg-orange-100">
                      {orderShortLabel(order)} · {order.customerName} · {getCriticality(order, Date.now()).label}
                    </button>
                  </li>
                ))}
                {demorados.length > 5 && <li className="px-1 text-xs font-semibold text-slate-500">+{demorados.length - 5} más</li>}
              </ul>
            </div>
          )}
          {errores.length > 0 && (
            <div className="rounded-2xl bg-white p-3 ring-1 ring-rose-200">
              <p className="mb-2 text-xs font-black uppercase tracking-wide text-rose-700">Con error</p>
              <ul className="grid gap-1.5">
                {errores.slice(0, 5).map(({ order, message }) => (
                  <li key={order.id}>
                    <button type="button" onClick={() => scrollToOrder(order.id)} className="w-full rounded-xl bg-rose-50 px-3 py-2 text-left text-sm font-semibold text-rose-900 hover:bg-rose-100">
                      <span className="block">{orderShortLabel(order)} · {order.customerName}</span>
                      <span className="block truncate text-xs font-normal text-rose-700">{message}</span>
                    </button>
                  </li>
                ))}
                {errores.length > 5 && <li className="px-1 text-xs font-semibold text-slate-500">+{errores.length - 5} más</li>}
              </ul>
            </div>
          )}
          {stockInsuficiente.length > 0 && (
            <div className="rounded-2xl bg-white p-3 ring-1 ring-rose-200">
              <p className="mb-2 text-xs font-black uppercase tracking-wide text-rose-700">Stock insuficiente</p>
              <ul className="grid gap-1.5">
                {stockInsuficiente.slice(0, 5).map((order) => (
                  <li key={order.id}>
                    <button type="button" onClick={() => scrollToOrder(order.id)} className="w-full rounded-xl bg-rose-50 px-3 py-2 text-left text-sm font-semibold text-rose-900 hover:bg-rose-100">
                      <span className="block">{orderShortLabel(order)} · {order.customerName}</span>
                      <span className="block truncate text-xs font-normal text-rose-700">
                        {stockShortageSummary(order)}
                      </span>
                    </button>
                  </li>
                ))}
                {stockInsuficiente.length > 5 && <li className="px-1 text-xs font-semibold text-slate-500">+{stockInsuficiente.length - 5} más</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function ReceptionPage() {
  const { user } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const [branchId, setBranchId] = useState(user?.branchId ?? '');
  const [actionId, setActionId] = useState<string | null>(null);
  const [editOrder, setEditOrder] = useState<OrderView | null>(null);
  const [editItems, setEditItems] = useState<EditableItem[]>([]);
  const [editReason, setEditReason] = useState('');
  const [editing, setEditing] = useState(false);
  const [mapsInputs, setMapsInputs] = useState<Record<string, string>>({});
  const [reasonInputs, setReasonInputs] = useState<Record<string, string>>({});
  const [deliveryFilter, setDeliveryFilter] = useState<DeliveryFilter>('todas');
  const [quickFilters, setQuickFilters] = useState<ReceptionQuickFilter[]>([]);
  const toggleQuickFilter = useCallback((filter: ReceptionQuickFilter | null) => {
    setQuickFilters((current) => filter === null
      ? []
      : current.includes(filter)
        ? current.filter((entry) => entry !== filter)
        : [...current, filter]);
  }, []);
  const { links: deliveryLinks, setLink, copyLink } = useDeliveryLinks();
  const [fulfillmentErrors, setFulfillmentErrors] = useState<Record<string, string>>({});
  const [fulfillmentAction, setFulfillmentAction] = useState<string | null>(null);

  const canChooseBranch = user?.role === 'owner' || user?.role === 'manager';

  const { data: allBranches = [] } = useBusinessBranches();
  const branches = useMemo(() => {
    if (!canChooseBranch && user?.branchId) {
      return [{ id: user.branchId, name: 'Sucursal asignada', active: true, whatsapp: null }];
    }
    return allBranches.filter((branch) => branch.active);
  }, [canChooseBranch, user?.branchId, allBranches]);

  useEffect(() => {
    if (!branchId && branches.length) setBranchId(branches[0].id);
  }, [branchId, branches]);

  const { data: orders = [], isLoading, isFetching } = useOrders(branchId, 'pendiente,preparacion,completado,cancelado');
  const loading = isLoading && !branches.length;
  const refreshing = isFetching && !isLoading;

  const invalidateOrdersRef = useRef(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
  });
  useEffect(() => {
    invalidateOrdersRef.current = () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    };
  }, [queryClient]);
  const streamStatus = useSse(branchId, () => { invalidateOrdersRef.current(); });

  const filteredOrders = useMemo(() => filterOrdersByDelivery(orders, deliveryFilter), [deliveryFilter, orders]);
  const quickFilteredOrders = useMemo(
    () => filteredOrders.filter((order) => matchesReceptionQuickFilter(order, quickFilters)),
    [filteredOrders, quickFilters],
  );
  const pending = useMemo(() => quickFilteredOrders.filter((order) => order.status === 'pendiente'), [quickFilteredOrders]);
  const preparing = useMemo(() => quickFilteredOrders.filter((order) => order.status === 'preparacion'), [quickFilteredOrders]);
  const fulfillmentQueue = useMemo(() => quickFilteredOrders.filter((order) => isFulfillmentStage(order.fulfillmentStage)), [quickFilteredOrders]);
  // The Requiere acción panel stays on the delivery-filtered list only: quick
  // filters shape the visible sections, the panel keeps reporting every
  // requiring-action order regardless of the active quick chips.
  const actionItems = useMemo(
    () => computeReceptionActionItems(filteredOrders, fulfillmentErrors, Date.now()),
    [filteredOrders, fulfillmentErrors],
  );
  const canManageTerminal = user?.role === 'owner' || user?.role === 'manager';

  const confirmLocation = useConfirmDeliveryLocation();
  const tokenMutation = useDeliveryTokenMutation();
  const deliverMutation = useFulfillmentDeliveryComplete();
  const handoffMutation = useFulfillmentHandoff();
  const correctionMutation = useFulfillmentCorrection();
  const cancelFulfillmentMutation = useLateFulfillmentCancellation();

  const transition = useCallback(async (order: OrderView, status: 'preparacion' | 'cancelado' | 'fantasma') => {
    let reason: string | undefined;
    if (status === 'cancelado') {
      reason = window.prompt('Motivo de cancelación:')?.trim();
      if (!reason) return;
    }
    if (status === 'fantasma') {
      reason = window.prompt('Motivo o intento de contacto (opcional):')?.trim() || 'Cliente sin comunicación o respuesta';
      if (!window.confirm(`Marcar el pedido #${String(order.correlation).padStart(2, '0')} como fantasma?`)) return;
    }
    setActionId(order.id);
    try {
      await apiRequest(`/api/orders/${order.id}/status`, { method: 'POST', body: jsonBody({ status, reason }) });
      show(status === 'preparacion' ? 'Pedido enviado a cocina.' : status === 'fantasma' ? 'Pedido marcado como fantasma.' : 'Pedido cancelado.', 'success');
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo actualizar el pedido.', 'error');
    } finally { setActionId(null); }
  }, [queryClient, show]);

  const openEdit = useCallback((order: OrderView) => {
    setEditOrder(order);
    setEditReason('Ajuste solicitado o validado por recepción');
    setEditItems(order.items.map((item) => ({
      itemId: item.id,
      productName: item.productName,
      quantity: item.quantity,
      ingredients: item.ingredients.filter((ingredient) => ingredient.ingredientId).map((ingredient) => ({
        ingredientId: ingredient.ingredientId as string,
        name: ingredient.name,
        included: ingredient.included,
        selectable: ingredient.selectable,
      })),
    })));
  }, []);

  const saveEdit = useCallback(async (event: FormEvent) => {
    event.preventDefault();
    if (!editOrder) return;
    setEditing(true);
    try {
      await apiRequest(`/api/orders/${editOrder.id}`, {
        method: 'PATCH',
        body: jsonBody({
          reason: editReason,
          items: editItems.map((item) => ({
            itemId: item.itemId,
            quantity: item.quantity,
            ingredients: item.ingredients.map((ingredient) => ({ ingredientId: ingredient.ingredientId, included: ingredient.included })),
          })),
        }),
      });
      setEditOrder(null);
      show('Pedido actualizado y cambio registrado en auditoría.', 'success');
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo modificar el pedido.', 'error');
    } finally { setEditing(false); }
  }, [editOrder, editReason, editItems, queryClient, show]);

  const handleFulfillmentError = useCallback(async (order: OrderView, error: unknown) => {
    const message = error instanceof Error ? error.message : 'No se pudo actualizar la entrega.';
    setFulfillmentErrors((current) => ({ ...current, [order.id]: message }));
    show(message, 'error');
    if (error instanceof ApiClientError && error.status === 409) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      show('El pedido cambió. La lista fue actualizada antes de reintentar.', 'error');
    }
  }, [queryClient, show]);

  const runFulfillment = useCallback(async (order: OrderView, key: string, action: () => Promise<unknown>, success: string) => {
    setFulfillmentAction(`${order.id}:${key}`);
    setFulfillmentErrors((current) => ({ ...current, [order.id]: '' }));
    try {
      await action();
      show(success, 'success');
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    } catch (error) {
      await handleFulfillmentError(order, error);
    } finally {
      setFulfillmentAction(null);
    }
  }, [handleFulfillmentError, queryClient, show]);

  const issueToken = useCallback((order: OrderView, action: 'issue' | 'rotate' | 'revoke') => {
    if (!order.fulfillmentStage) return;
    void runFulfillment(order, `token-${action}`, async () => {
      const result = await tokenMutation.mutateAsync({ orderId: order.id, expectedStage: order.fulfillmentStage as FulfillmentStage, action });
      const rawToken = result.credential?.rawToken;
      if (rawToken) {
        setLink(order.id, deliveryLinkUrl(window.location.origin, rawToken));
      }
      if (action === 'revoke') {
        setLink(order.id, '');
      }
    }, action === 'revoke'
      ? 'Enlace revocado.'
      : action === 'rotate'
        ? 'Enlace reemitido. El enlace anterior dejó de ser válido.'
        : 'Enlace de reparto emitido. Copialo ahora; no se volverá a mostrar.');
  }, [runFulfillment, tokenMutation, setLink]);

  if (loading) return <LoadingScreen label="Abriendo recepción…" />;

  return (
    <div>
      <PageHeader
        title="Recepción de pedidos"
        description="Valida el contacto, ajusta ingredientes existentes y envía los pedidos a preparación."
        action={(
          <div className="flex flex-wrap items-center gap-2">
            <span className={cx('inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold', streamStatus === 'connected' ? 'bg-emerald-100 text-emerald-800' : streamStatus === 'error' ? 'bg-rose-100 text-rose-800' : 'bg-slate-200 text-slate-700')}>
              <span className={cx('h-2 w-2 rounded-full', streamStatus === 'connected' ? 'bg-emerald-500' : streamStatus === 'error' ? 'bg-rose-500' : 'bg-slate-500')} />
              {streamStatus === 'connected' ? 'Tiempo real activo' : streamStatus === 'error' ? 'Reconectando' : 'Conectando'}
            </span>
            <Button variant="secondary" busy={refreshing} onClick={() => void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all })}>Actualizar</Button>
          </div>
        )}
      />

      <Card className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <HeroSelectField
              label="Sucursal"
              value={branchId || null}
              isDisabled={!canChooseBranch}
              placeholder="Sin sucursales activas"
              onChange={(value) => setBranchId(String(value ?? ''))}
              className="min-w-64"
              options={branches.map((branch) => ({ id: branch.id, label: branch.name }))}
            />
            <HeroSelectField
              label="Modalidad"
              value={deliveryFilter}
              onChange={(value) => setDeliveryFilter(String(value ?? 'todas') as DeliveryFilter)}
              className="min-w-64"
              aria-label="Filtrar por modalidad"
              options={[
                { id: 'todas', label: 'Todas las modalidades' },
                { id: 'delivery', label: 'Delivery' },
                { id: 'pickup', label: 'Retiro' },
                { id: 'local', label: 'En el sitio' },
              ]}
            />
          </div>
          <div className="flex gap-4 text-sm"><div><span className="text-slate-500">Pendientes</span><strong className="ml-2 text-lg">{pending.length}</strong></div><div><span className="text-slate-500">En cocina</span><strong className="ml-2 text-lg">{preparing.length}</strong></div></div>
        </div>
        <div className="mt-5 border-t border-slate-200 pt-4">
          <ReceptionQuickFilters active={quickFilters} onToggle={toggleQuickFilter} />
        </div>
      </Card>

      <RequiereAccionPanel groups={actionItems} />

      <section id="pending-section">
        <h2 className="mb-3 text-lg font-black text-slate-950">Pendientes de validación</h2>
        {pending.length ? <div className="grid gap-4">{pending.map((order) => (
          <Card key={order.id} id={`order-${order.id}`} className="overflow-hidden p-0">
            <div className="grid gap-4 p-4 md:grid-cols-[auto_1fr_auto] md:items-start">
              <OrderNumber value={order.correlation} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2"><h3 className="text-lg font-black">{order.customerName}</h3><Badge tone={statusTone[order.status]}>{order.status}</Badge>{order.modifiedByReception && <Badge tone="purple">modificado</Badge>}</div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500"><span>☎ {order.customerPhone}</span><span>{order.deliveryType === 'delivery' ? 'Delivery' : order.deliveryType === 'pickup' ? 'Retiro' : 'En el sitio'}</span><span>{new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></div>
                {order.deliveryAddress && <p className="mt-2 rounded-lg bg-slate-50 p-2 text-sm"><strong>Dirección:</strong> {order.deliveryAddress}</p>}
                <div className="mt-3 grid gap-2">
                   {order.items.map((item) => (
                     <div key={item.id} className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-sm">
                       <div className="flex flex-wrap items-center gap-2">
                         <strong>{item.quantity}× {item.productName}</strong>
                         {item.variantName && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-bold text-violet-800">Variante: {item.variantName}</span>}
                         {item.productType === 'combo' && <Badge tone="purple">combo</Badge>}
                       </div>
                       <ItemConfiguration item={item} />
                     </div>
                   ))}
                </div>
                {order.notes && <p className="mt-2 text-sm text-slate-600"><strong>Nota:</strong> {order.notes}</p>}
              </div>
              <div className="md:text-right"><div className="text-xl font-black">{formatUsd(order.totalUsdCents)}</div><div className="text-sm font-bold text-slate-500">{formatLocalMinor(order.totalLocalMinor, order.localCurrency, order.currencyDecimals, order.currencySymbol)}</div></div>
            </div>
            <div className="flex flex-wrap gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
              <a href={`https://wa.me/${phoneForWhatsapp(order.customerPhone)}?text=${encodeURIComponent(whatsappMessage(order))}`} target="_blank" rel="noreferrer" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">◉ WhatsApp</a>
              <Button variant="secondary" onClick={() => openEdit(order)}>✎ Editar</Button>
              <Button variant="success" busy={actionId === order.id} onClick={() => void transition(order, 'preparacion')}>Enviar a cocina</Button>
              <Button variant="ghost" disabled={actionId === order.id} onClick={() => void transition(order, 'fantasma')} title="Cliente que nunca se comunicó ni respondió">👻 Fantasma</Button>
              <Button variant="danger" disabled={actionId === order.id} onClick={() => void transition(order, 'cancelado')}>Cancelar</Button>
            </div>
          </Card>
        ))}</div> : <EmptyState title="Sin pedidos pendientes" description="Los pedidos nuevos aparecerán aquí automáticamente mediante SSE." />}
      </section>

      {preparing.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 text-lg font-black text-slate-950">En preparación</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{preparing.map((order) => (
            <Card key={order.id} className="flex items-center gap-4"><OrderNumber value={order.correlation} /><div className="min-w-0"><h3 className="truncate font-black">{order.customerName}</h3><p className="text-sm text-slate-500">{order.items.length} línea(s) · {formatUsd(order.totalUsdCents)}</p><Badge tone="blue">en cocina</Badge></div></Card>
          ))}</div>
        </section>
      )}

      <section className="mt-8">
        <h2 className="mb-3 text-lg font-black text-slate-950">Listos y entrega</h2>
        {fulfillmentQueue.length ? <div className="grid gap-4">{fulfillmentQueue.map((order) => {
          const stage = order.fulfillmentStage as FulfillmentStage;
          const mapsValue = mapsInputs[order.id] ?? order.confirmedMapsUrl ?? '';
          const savedMapsUrl = extractAllowedGoogleMapsUrl(order.deliveryAddress);
          const reasonValue = reasonInputs[order.id] ?? '';
          const error = fulfillmentErrors[order.id];
          const busy = fulfillmentAction?.startsWith(`${order.id}:`) ?? false;
          const canUpdateDeliveryLocation = order.deliveryType === 'delivery' && (stage === 'ready' || stage === 'transit');
          const canIssueLink = canIssueDeliveryLink(order.deliveryType, stage);
          const canConfirmDelivered = order.deliveryType === 'delivery' && stage === 'transit';
          const canHandoff = (order.deliveryType === 'pickup' || order.deliveryType === 'local') && stage === 'ready';
          return (
            <Card key={order.id} id={`order-${order.id}`} className="grid gap-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2"><OrderNumber value={order.correlation} /><div><h3 className="text-lg font-black">{order.customerName}</h3><p className="text-sm text-slate-500">{order.deliveryType === 'delivery' ? 'Delivery' : order.deliveryType === 'pickup' ? 'Retiro' : 'En el sitio'} · {order.customerPhone}</p></div></div>
                </div>
                <Badge tone={fulfillmentTone[stage]}>{fulfillmentLabels[stage]}</Badge>
              </div>
              <p className="text-sm text-slate-600"><strong>Estado visible:</strong> {fulfillmentLabels[stage]}</p>
              {order.deliveryAddress && <p className="rounded-xl bg-slate-50 p-3 text-sm"><strong>Dirección:</strong> {order.deliveryAddress}</p>}
              {error && <div role="alert" aria-live="assertive" className="rounded-xl bg-rose-50 p-3 text-sm font-bold text-rose-800">{error}</div>}
              {order.deliveryType === 'delivery' && (
                <div className="grid gap-3 lg:grid-cols-[1fr_auto_auto] lg:items-end">
                  <Field label={`URL de Google Maps para pedido #${String(order.correlation).padStart(2, '0')}`} hint="Debe ser HTTPS y pertenecer a google.com, www.google.com, maps.google.com o maps.app.goo.gl.">
                    <Input value={mapsValue} disabled={busy || stage === 'delivered' || stage === 'cancelled'} onChange={(event) => setMapsInputs((current) => ({ ...current, [order.id]: event.target.value }))} placeholder="https://maps.google.com/..." />
                  </Field>
                  <Button variant="secondary" disabled={!canUpdateDeliveryLocation || !savedMapsUrl || busy} busy={fulfillmentAction === `${order.id}:saved-location`} onClick={() => {
                    if (!savedMapsUrl) return;
                    setMapsInputs((current) => ({ ...current, [order.id]: savedMapsUrl }));
                    void runFulfillment(order, 'saved-location', () => confirmLocation.mutateAsync({ orderId: order.id, expectedStage: stage, mapsUrl: savedMapsUrl }), 'Ubicación guardada confirmada.');
                  }}>Usar ubicación guardada</Button>
                  <Button disabled={!canUpdateDeliveryLocation || !mapsValue.trim()} busy={fulfillmentAction === `${order.id}:location`} onClick={() => void runFulfillment(order, 'location', () => confirmLocation.mutateAsync({ orderId: order.id, expectedStage: stage, mapsUrl: mapsValue.trim() }), 'Ubicación confirmada.')}>Confirmar ubicación</Button>
                </div>
              )}
              {order.deliveryType === 'delivery' && (
                <div className="grid gap-2 rounded-2xl bg-slate-50 p-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-slate-500">Enlace de reparto</p>
                    {deliveryLinks[order.id] ? (
                      <p className="mt-1 break-all rounded-xl bg-white p-3 text-sm font-semibold text-slate-700"><strong>Enlace:</strong> {deliveryLinks[order.id]}</p>
                    ) : (
                      <p className="mt-1 rounded-xl bg-white p-3 text-sm text-slate-500">Confirma la ubicación y luego confirma el reparto para generar el enlace. Por seguridad, el enlace completo solo se muestra al emitirlo.</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button disabled={!order.confirmedMapsUrl || !canIssueLink} busy={fulfillmentAction === `${order.id}:token-issue`} onClick={() => issueToken(order, 'issue')}>Confirmar reparto</Button>
                    {deliveryLinks[order.id] && <Button variant="secondary" onClick={() => void copyLink(order.id)}>Copiar enlace</Button>}
                    <Button variant="danger" disabled={stage === 'delivered' || stage === 'cancelled'} busy={fulfillmentAction === `${order.id}:token-revoke`} onClick={() => issueToken(order, 'revoke')}>Revocar reparto</Button>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {canConfirmDelivered && <Button variant="success" busy={fulfillmentAction === `${order.id}:deliver`} onClick={() => void runFulfillment(order, 'deliver', () => deliverMutation.mutateAsync({ orderId: order.id, expectedStage: stage }), 'Entrega delivery registrada.')}>Confirmar entregado</Button>}
                {canHandoff && <Button variant="success" busy={fulfillmentAction === `${order.id}:handoff`} onClick={() => void runFulfillment(order, 'handoff', () => handoffMutation.mutateAsync({ orderId: order.id, expectedStage: stage }), 'Entrega en mostrador registrada.')}>Registrar entrega</Button>}
                {canManageTerminal && (stage === 'delivered' || stage === 'handed_off') && <Button variant="secondary" disabled={reasonValue.trim().length < 3} busy={fulfillmentAction === `${order.id}:correct`} onClick={() => void runFulfillment(order, 'correct', () => correctionMutation.mutateAsync({ orderId: order.id, expectedStage: stage, reason: reasonValue.trim() }), 'Pedido corregido a listo.')}>Corregir a listo</Button>}
                {canManageTerminal && (stage === 'ready' || stage === 'transit') && <Button variant="danger" disabled={reasonValue.trim().length < 3} busy={fulfillmentAction === `${order.id}:cancel`} onClick={() => void runFulfillment(order, 'cancel', () => cancelFulfillmentMutation.mutateAsync({ orderId: order.id, expectedStage: stage, reason: reasonValue.trim() }), 'Pedido cancelado y enlace revocado.')}>Cancelación tardía</Button>}
              </div>
              {canManageTerminal && (stage === 'ready' || stage === 'transit' || stage === 'delivered' || stage === 'handed_off') && (
                <Field label="Motivo para corrección o cancelación tardía" hint="Requerido solo para acciones privilegiadas.">
                  <Textarea minLength={3} maxLength={300} value={reasonValue} onChange={(event) => setReasonInputs((current) => ({ ...current, [order.id]: event.target.value }))} />
                </Field>
              )}
            </Card>
          );
        })}</div> : <EmptyState title="Sin pedidos listos" description="Los pedidos listos para entrega aparecerán aquí cuando cocina termine todas sus tareas." />}
      </section>

      <Modal open={Boolean(editOrder)} onClose={() => setEditOrder(null)} title={editOrder ? `Editar pedido #${String(editOrder.correlation).padStart(2, '0')}` : 'Editar pedido'} width="max-w-3xl">
        <form onSubmit={saveEdit} className="grid gap-5">
          <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900">Solo puedes cambiar cantidades e incluir o excluir ingredientes ya registrados. No se permiten productos ni ingredientes inventados.</div>
          {editItems.map((item, itemIndex) => (
            <Card key={item.itemId} className="grid gap-3 shadow-none">
              <div className="flex items-center justify-between gap-3"><h3 className="font-black">{item.productName}</h3><div className="flex items-center gap-2"><button type="button" className="grid h-9 w-9 place-items-center rounded-full border" onClick={() => setEditItems((current) => current.map((value, index) => index === itemIndex ? { ...value, quantity: Math.max(1, value.quantity - 1) } : value))}>−</button><strong className="w-8 text-center">{item.quantity}</strong><button type="button" className="grid h-9 w-9 place-items-center rounded-full border" onClick={() => setEditItems((current) => current.map((value, index) => index === itemIndex ? { ...value, quantity: Math.min(99, value.quantity + 1) } : value))}>+</button></div></div>
              {editOrder && <SavedItemConfiguration order={editOrder} itemId={item.itemId} />}
              {item.ingredients.length ? <div className="grid gap-2 sm:grid-cols-2">{item.ingredients.map((ingredient, ingredientIndex) => (
                <Checkbox key={ingredient.ingredientId} label={ingredient.name} isSelected={ingredient.included} isDisabled={!ingredient.selectable} onChange={(included) => setEditItems((current) => current.map((value, index) => index === itemIndex ? { ...value, ingredients: value.ingredients.map((entry, nestedIndex) => nestedIndex === ingredientIndex ? { ...entry, included } : entry) } : value))} className={cx('rounded-xl border border-slate-200 p-3 text-sm font-medium', !ingredient.selectable && 'bg-slate-50 opacity-65')} />
              ))}</div> : <p className="text-sm text-slate-500">Sin ingredientes configurables.</p>}
            </Card>
          ))}
          <Field label="Motivo del cambio"><Textarea required minLength={3} maxLength={300} value={editReason} onChange={(event) => setEditReason(event.target.value)} /></Field>
          <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setEditOrder(null)}>Cancelar</Button><Button type="submit" busy={editing}>Guardar modificación</Button></div>
        </form>
      </Modal>
    </div>
  );
}
